import { IJobTitleState } from 'store/types';
import { appConfig } from '../config/app';
import {
  ApiQueryOperator,
  IAcceptanceLedgerEntry,
  IAcceptancePayment,
  IAcceptancePaymentStats,
  IAchTransaction,
  IApiList,
  ICalculatorRules,
  ICreateUpdateCalculatorRules,
  ICreateUpdateDaySegmentRequest,
  IDaySegment,
  IEmployee,
  IEmployeePayrollTip,
  IFeeLedgerEntry,
  IGetEmployeePayoutsResponse,
  IGetPayPeriodsResponse,
  ILedgerEntry,
  IPaymentSet,
  IPayout,
  IPayoutStats,
  ISite,
  ISiteBalance,
  ITipAcceptancePaymentSet,
  ITipAcceptancePaymentSetPayouts,
} from '../types';
import { ApiClient, IApiQueryOptions } from './ApiClient';

export const Sites = {
  listJobs: (siteId: string, includeInactive?: boolean, options?: IApiQueryOptions) =>
    ApiClient.get<IApiList<IJobTitleState>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/jobs`, {
      ...options,
      limit: 0,
      ...(!includeInactive && { q: [{ key: 'count', operator: ApiQueryOperator.gt, value: '0' }] }),
    }),
  show: (siteId: string) => ApiClient.get<ISite>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}`),
  showSiteBalance: (siteId: string) => ApiClient.get<ISiteBalance>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/balance`),
  listEmployees: (siteId: string, options?: IApiQueryOptions) => ApiClient.get<IApiList<IEmployee>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/employees`, options),

  // Day Segments
  generateDaySegments: (siteId: string) => ApiClient.post<IDaySegment[]>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/ai/day_segments`, undefined),
  createDaySegment: (siteId: string, body: ICreateUpdateDaySegmentRequest) => ApiClient.post<IDaySegment>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/day_segments`, body),
  getDaySegments: (siteId: string, options?: IApiQueryOptions) => ApiClient.get<IApiList<IDaySegment>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/day_segments`, options),
  updateDaySegment: (siteId: string, daySegmentId: string, body: ICreateUpdateDaySegmentRequest) => ApiClient.put<IDaySegment>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/day_segments/${daySegmentId}`, body),
  deleteDaySegment: (siteId: string, daySegmentId: string) => ApiClient.delete<IDaySegment>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/day_segments/${daySegmentId}`),
  getDaySegmentById: (siteId: string, daySegmentId: string) => ApiClient.get<IDaySegment>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/day_segments/${daySegmentId}`),

  // Ledger
  listPrefundLedgerEntries: (siteId: string, startDate: string, endDate: string, options?: IApiQueryOptions) =>
    ApiClient.get<IApiList<ILedgerEntry>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/ledgers/prefund/${startDate}/${endDate}`, { ...options }),
  listAcceptanceLedgerEntries: (siteId: string, startDate: string, endDate: string, options?: IApiQueryOptions) =>
    ApiClient.get<IApiList<IAcceptanceLedgerEntry>>(`${appConfig.API_BASE_URL}/acceptance/sites/${siteId}/ledgers/acceptance/${startDate}/${endDate}`, { ...options }),
  listFeeLedgerEntries: (siteId: string, startDate: string, endDate: string, options?: IApiQueryOptions) =>
    ApiClient.get<IApiList<IFeeLedgerEntry>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/ledgers/fees/${startDate}/${endDate}`, { ...options }),

  // Calculator rules
  createUpdateCalculatorRules: (siteId: string, body: ICreateUpdateCalculatorRules) => ApiClient.put<ICalculatorRules>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/calculator`, body),
  generateCalculatorRules: (siteId: string) => ApiClient.post<ICreateUpdateCalculatorRules>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/ai/calculator/rules`, undefined),
  listCalculatorRules: (siteId: string) => ApiClient.get<ICalculatorRules>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/calculator`),

  // Payouts
  getPayouts: (siteId: string, options: IApiQueryOptions) => ApiClient.get<IApiList<IPayout>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/payouts`, options),
  getPayoutsStats: (siteId: string, startDate: string, endDate: string, options?: IApiQueryOptions) => ApiClient.get<IPayoutStats>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/payouts/${startDate}/${endDate}/stats`, options),
  getEmployeePayrollTips: (siteId: string, startDate: string, endDate: string, options: IApiQueryOptions) =>
    ApiClient.get<IApiList<IEmployeePayrollTip>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/payouts/employee/${startDate}/${endDate}/payment_date`, options),
  getEmployeePayouts: (siteId: string, employeeId: string, options: IApiQueryOptions) => ApiClient.get<IGetEmployeePayoutsResponse>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/employee/${employeeId}/payouts`, options),
  getPayPeriod: (siteId: string) => ApiClient.get<IGetPayPeriodsResponse>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/pay_periods`),

  // Payments
  getSitePayments: (siteId: string, options?: IApiQueryOptions) => ApiClient.get<IApiList<IPaymentSet>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/payments`, options),

  // Acceptance payments
  getTipAcceptancePayments: (siteId: string, options?: IApiQueryOptions) => ApiClient.get<IApiList<IAcceptancePayment>>(`${appConfig.API_BASE_URL}/acceptance/sites/${siteId}/payments`, options),
  getTipAcceptancePaymentsSets: (siteId: string, options?: IApiQueryOptions) => ApiClient.get<IApiList<ITipAcceptancePaymentSet>>(`${appConfig.API_BASE_URL}/acceptance/sites/${siteId}/payment_sets`, options),
  getTipAcceptancePayouts: (siteId: string, paymentSetId: string) => ApiClient.get<ITipAcceptancePaymentSetPayouts>(`${appConfig.API_BASE_URL}/acceptance/sites/${siteId}/payment_sets/${paymentSetId}/payouts`),
  getTipAcceptancePaymentStats: (siteId: string, startDate: string, endDate: string, options?: IApiQueryOptions) =>
    ApiClient.get<IAcceptancePaymentStats>(`${appConfig.API_BASE_URL}/acceptance/sites/${siteId}/payments/stats/${startDate}/${endDate}/payment_date`, options),

  // ACH
  listAchTransactions: (siteId: string, options?: IApiQueryOptions) => ApiClient.get<IApiList<IAchTransaction>>(`${appConfig.API_BASE_URL}/v1/sites/${siteId}/ach/transactions`, options),
};
